import { getDownstream, getPreprocessDownstream } from './filterModuleStoreAccessors';
import { defaultFilterGroups, uuidSingleton } from 'components/filter/filters-next/defaults';
import { transformToFormOperator } from 'components/filter/filters-next/readable';
import { getResourceFromRelationalKey } from 'components/filter/filters-next/relational';
import { getColumnFilterKey, prefixDynamicFilter } from 'components/filter/filters-next/util';
import {
    ComparisonFilterDefinition,
    FilterDefinition,
    FilterGroup,
    FilterGroupRequest,
    FilterOperator,
    FilterRequestMap,
    FilterRequestValue,
    FilterValue,
    NegationOperator,
    RelationalServerKey,
    ServerKey,
} from 'components/filter/types';
import { first } from 'lodash';
import { Settings } from 'luxon';
import { Column, ReportResource } from 'types/domain';
import { FiltersRequestNext, SavedNextFilters } from 'types/request';
import { v4 as uuid } from 'uuid';

type FilterType = FilterGroupRequest<any>;
type FilterKeys = { key?: string; relationalKey?: string };
type DonorSearchFilterRequestValue = FilterRequestValue<string> & {
    key: ServerKey;
};
type FilterTransformFn = <T>(key: ServerKey, filters: FilterRequestValue<T>) => void;
type ChildrenTransformFn = (filter: FilterType, keys?: FilterKeys) => void;

const transformPartitionedSavedFilters = (filters: SavedNextFilters): FilterGroup[] => {
    if (Array.isArray(filters) && filters.length === 0) {
        return defaultFilterGroups;
    }

    const { global, ...rest } = filters;
    const nextGlobal =
        global.length > 0 && Array.isArray(global[0].children) ? global[0].children : global;

    return [
        {
            id: uuidSingleton,
            innerOperator: 'and',
            outerOperator: 'and',
            filters: [...transformSavedFilters(nextGlobal), ...transformNextSavedFilters(rest)],
        },
    ];
};

const transformSavedFilterGroups = (filters: FilterType[]): FilterGroup[] => {
    if (filters.length === 0) {
        return defaultFilterGroups;
    }

    return filters.map(f => ({
        id: uuid(),
        innerOperator: (first(f.children) as FilterRequestValue<any> | undefined)?.type ?? 'and',
        outerOperator: f.type,
        filters: transformSavedFilters(f.children),
    }));
};

const transformNextSavedFilters = (filters: FiltersRequestNext): FilterValue[] =>
    Object.entries(filters).reduce((prev, [_key, filter]) => {
        const key = prefixDynamicFilter(_key as ServerKey);
        const flattened: FilterValue[] = filter.map(f => getNextFilter(f, key));
        return [...prev, ...flattened];
    }, [] as FilterValue[]);

const transformSavedColumnFilters = (
    filters: FilterDefinition[],
    resource: ReportResource,
    column: Column,
): FilterValue[] =>
    filters.map(filter => {
        const key = getColumnFilterKey(column.name, filter.type, resource);
        return getNextFilter(filter, key);
    });

const getNextFilter = (filter: FilterDefinition, key: ServerKey) => {
    const transform = getDownstream(key);
    const preprocess = getPreprocessDownstream(key);
    const _nextValue = getNextValueByType(filter);
    const nextValue = preprocess
        ? preprocess({
              value: _nextValue,
              operator: getNextOperatorByType(filter),
              tz: Settings.defaultZone,
          } as FilterRequestValue<string | string[]> & { tz?: string })
        : _nextValue;

    return {
        id: uuid(),
        isLegacy: false,
        negationOperator: undefined,
        serverKey: key,
        filterOperator: getNextOperatorByType(filter),
        value: transform ? transform(nextValue, key) : nextValue,
    };
};

const getNextValueByType = (filter: FilterDefinition) => {
    switch (filter.type) {
        case 'between':
            return filter.values;
        case 'date_range':
            return filter.date_range.range;
        case 'comparison':
            return filter.value;
    }
};

const getNextOperatorByType = (filter: FilterDefinition) => {
    switch (filter.type) {
        case 'between':
            return 'bet';
        case 'date_range':
            return filter.date_range.type;
        case 'comparison':
            return transformNextOperator(filter.operator);
    }
};

const transformNextOperator = (
    operator: ComparisonFilterDefinition['operator'],
): FilterOperator => {
    switch (operator) {
        case '!=':
            return 'neq';
        case '>':
            return 'gt';
        case '<':
            return 'lt';
        case '>=':
            return 'gte';
        case '<=':
            return 'lte';
        case '=':
        default:
            return 'eq';
    }
};

const transformSavedFilters = (
    filters: FilterType[] | FilterRequestValue<unknown>,
    keys?: FilterKeys,
): FilterValue[] => {
    const { key, relationalKey } = keys ?? {};
    const transformedFilters: FilterValue[] = [];
    const filterKeys = Object.keys(filters);

    const transformNestedChildren: ChildrenTransformFn = (filter, keys) => {
        transformedFilters.push(...transformSavedFilters(filter.children[0], keys));
    };

    const transformStructuredChildren: ChildrenTransformFn = (filter, keys) => {
        Object.entries(filter.children).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach(v => {
                    transformedFilters.push(...transformSavedFilters(v, { ...keys, key }));
                });
            }
        });
    };

    const processFilters = (filters: FilterType[]) =>
        filters.forEach(filter => {
            if (typeof filter.children === 'object' && filter.children !== null) {
                if (Array.isArray(filter.children)) {
                    transformNestedChildren(filter, keys);
                } else {
                    transformStructuredChildren(filter, keys);
                }
            }
        });

    const transformRelationalFilters: FilterTransformFn = (key, filters) => {
        if (key.includes('has_') || key.includes('doesnt_have_')) {
            transformedFilters.push(
                ...transformSavedFilters([filters as unknown as FilterType], {
                    relationalKey: key,
                    key: undefined,
                }),
            );
        }
    };

    const transformDonorSearchFilters: FilterTransformFn = (key, filters) => {
        if (key.includes('donorsearch')) {
            const { key: donorsearchKey, ...rest } =
                filters as unknown as DonorSearchFilterRequestValue;
            transformedFilters.push(
                ...transformSavedFilters(rest, {
                    relationalKey,
                    key: donorsearchKey,
                }),
            );
        }
    };

    const transformFilters: FilterTransformFn = (key: ServerKey, filters) => {
        const transform = getDownstream(key);
        if (transform) {
            const preprocess = getPreprocessDownstream(key);
            const value = preprocess ? preprocess(filters) : filters.value;
            const negationOperator: NegationOperator = relationalKey?.includes('doesnt_have_')
                ? 'doesnt_have'
                : 'has';
            const coercedRelationalKey = relationalKey
                ? (`has_${getResourceFromRelationalKey(
                      relationalKey as RelationalServerKey,
                  )}` as RelationalServerKey)
                : undefined;

            transformedFilters.push({
                id: uuid(),
                isLegacy: true,
                value: Array.isArray(value)
                    ? value.map(v => transform(v, key))
                    : transform(value, key),
                serverKey: key,
                relationalServerKey: coercedRelationalKey,
                filterOperator: filters.operator
                    ? transformToFormOperator(filters.operator)
                    : undefined,
                negationOperator,
            });
        }
    };

    const transformFilterRequestValue: FilterTransformFn = (key, filters) => {
        transformRelationalFilters(key, filters);
        transformDonorSearchFilters(key, filters);
        transformFilters(key, filters);
    };

    if (Array.isArray(filters)) {
        processFilters(filters);
    } else if (key) {
        transformFilterRequestValue(key as ServerKey, filters);
    } else if (filterKeys.length > 0) {
        const conformedFilters = filterKeys.map(key => ({
            children: {
                [key]: (filters as unknown as FilterRequestMap)[key],
            },
        }));
        processFilters(conformedFilters as FilterType[]);
    }

    return transformedFilters;
};

export {
    transformSavedFilters,
    transformNextSavedFilters,
    transformSavedFilterGroups,
    transformPartitionedSavedFilters,
    transformSavedColumnFilters,
};
